import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  MenuItem, 
  Alert,
  Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import { trackLead } from '../../utils/analytics';

// Simplified form validation schema - only essential fields
const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  phone: Yup.string()
    .matches(/^[\d\s\-\(\)]+$/, 'Please enter a valid phone number')
    .min(10, 'Phone number must be at least 10 digits')
    .required('Phone number is required'),
  serviceType: Yup.string().required('Please select a service'),
  message: Yup.string()
});

// Service types
const serviceTypes = [
  { value: 'delivery', label: 'Delivery Services' },
  { value: 'junk-removal', label: 'Junk Removal' },
  { value: 'moving', label: 'Moving Services' },
  { value: 'specialty', label: 'Specialty Services' }
];

// Optional "How did you hear about us?" answers, so we know which marketing brings in jobs
const leadSources = [
  'Google search',
  'Google Maps',
  'Facebook',
  'Friend or family',
  'Saw our truck or trailer',
  'Returning customer',
  'Other'
];

interface QuoteRequestFormProps {
  // Preselects the service, e.g. 'junk-removal' on junk removal pages
  defaultService?: string;
}

const QuoteRequestForm: React.FC<QuoteRequestFormProps> = ({ defaultService = '' }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Form handling with Formik - simplified to single step
  const formik = useFormik({
    initialValues: {
      name: '',
      phone: '',
      serviceType: defaultService,
      message: '',
      source: ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const response = await fetch('/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            'form-name': 'quote-request',
            ...values
          }).toString()
        });

        if (response.ok) {
          setIsSubmitted(true);
          trackLead('quote-request', values.serviceType);
        } else {
          setSubmitError('Something went wrong. Please try again or call us directly.');
        }
      } catch (error) {
        setSubmitError('Something went wrong. Please try again or call us directly.');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleReset = () => {
    setIsSubmitted(false);
    formik.resetForm();
  };
  
  // Form submission success message
  const SuccessMessage = () => (
    <Box textAlign="center" py={5}>
      <Alert severity="success" sx={{ mb: 4 }}>
        Your quote request has been submitted successfully!
      </Alert>
      <Typography variant="h5" gutterBottom>
        Thank You for Contacting JBR Delivery LLC
      </Typography>
      <Typography variant="body1" paragraph>
        We've received your quote request and will get back to you within 24 hours.
      </Typography>
      <Button 
        variant="contained" 
        color="primary" 
        onClick={handleReset}
        startIcon={<SendIcon />}
      >
        Submit Another Request
      </Button>
    </Box>
  );

  return (
    <Box id="quote" sx={{ py: 10, backgroundColor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Box textAlign="center" mb={8}>
          <Typography
            variant="h2"
            component="h2"
            fontWeight={700}
            gutterBottom
            sx={{ mb: 3 }}
          >
            Request a Free Quote
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: '850px',
              mx: 'auto',
              px: { xs: 2, sm: 4 },
              lineHeight: 1.6
            }}
          >
            Fill out this quick form and we'll get back to you within 24 hours with a free, no-obligation quote.
          </Typography>
        </Box>
        
        <Grid container spacing={6}>
          <Grid item xs={12} lg={7}>
            <Paper elevation={3} sx={{ p: { xs: 3, sm: 5 }, overflow: 'hidden' }}>
              {!isSubmitted ? (
                <form
                  name="quote-request"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  onSubmit={formik.handleSubmit}
                >
                  <input type="hidden" name="form-name" value="quote-request" />
                  <p hidden>
                    <label>
                      Don't fill this out: <input name="bot-field" />
                    </label>
                  </p>
                  {submitError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {submitError}
                    </Alert>
                  )}
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="name"
                        name="name"
                        label="Your Name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="phone"
                        name="phone"
                        label="Phone Number"
                        value={formik.values.phone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.phone && Boolean(formik.errors.phone)}
                        helperText={formik.touched.phone && formik.errors.phone}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        id="serviceType"
                        name="serviceType"
                        select
                        label="What do you need help with?"
                        value={formik.values.serviceType}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.serviceType && Boolean(formik.errors.serviceType)}
                        helperText={formik.touched.serviceType && formik.errors.serviceType}
                        variant="outlined"
                      >
                        {serviceTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        id="message"
                        name="message"
                        label="Tell us about your project (optional)"
                        multiline
                        rows={3}
                        value={formik.values.message}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        variant="outlined"
                        placeholder="E.g., I need to move a couch from Appleton to Green Bay next Saturday"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        id="source"
                        name="source"
                        select
                        label="How did you hear about us?"
                        helperText="Optional"
                        value={formik.values.source}
                        onChange={formik.handleChange}
                        variant="outlined"
                      >
                        {leadSources.map((source) => (
                          <MenuItem key={source} value={source}>
                            {source}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        size="large"
                        fullWidth
                        disabled={isSubmitting}
                        startIcon={<SendIcon />}
                        sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 600 }}
                      >
                        {isSubmitting ? 'Sending...' : 'Get My Free Quote'}
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        By submitting, you agree to our{' '}
                        <Link component={RouterLink} to="/privacy-policy">Privacy Policy</Link>
                      </Typography>
                    </Grid>
                  </Grid>
                </form>
              ) : (
                <SuccessMessage />
              )}
            </Paper>
          </Grid>
          
          <Grid item xs={12} lg={5}>
            <Box
              sx={{
                backgroundColor: 'secondary.main',
                color: 'white',
                p: { xs: 3, sm: 4 },
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h4" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
                Contact Us Directly
              </Typography>
              
              <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                Need immediate assistance or have questions? Reach out to us directly and we'll be happy to help.
              </Typography>
              
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  mb: 3,
                  mt: 3
                }}
              >
                <PhoneIcon sx={{ mr: 2, color: 'primary.main', fontSize: 30, mt: 0.5 }} />
                <Box sx={{ overflow: 'hidden' }}>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    Call Us At
                  </Typography>
                  <Typography variant="h6" component="p" sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    <Link href="tel:9202558952" color="inherit" underline="hover">(920) 255-8952</Link>
                  </Typography>
                  <Typography variant="h6" component="p" sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    <Link href="tel:9202654729" color="inherit" underline="hover">(920) 265-4729</Link>
                  </Typography>
                </Box>
              </Box>
              
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  mb: 3
                }}
              >
                <EmailIcon sx={{ mr: 2, color: 'primary.main', fontSize: 30, mt: 0.5 }} />
                <Box sx={{ overflow: 'hidden' }}>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    Email Us At
                  </Typography>
                  <Typography variant="h6" component="p" sx={{
                    wordBreak: 'break-word'
                  }}>
                    jbrdeliveryllc@gmail.com
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ mt: 'auto', pt: 3 }}>
                <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ fontWeight: 600 }}>
                  Business Hours:
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Monday - Friday: 8 AM - 6 PM
                </Typography>
                <Typography variant="body1">
                  Saturday: 9 AM - 4 PM
                </Typography>
                <Typography variant="body1">
                  Sunday: Closed
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default QuoteRequestForm;
